import { useMemo, useState } from 'react';
import Stack from '@mui/material/Stack';
import Grid from '@mui/material/Grid2';
import Alert from '@mui/material/Alert';
import Typography from '@mui/material/Typography';
import { PageSection, StatTile } from '@radiology/ui';
import { ClipboardText, TickCircle, CloseCircle, Wallet3 } from 'iconsax-react';
import { RequestTable } from './RequestTable';
import { DecisionDialog } from './DecisionDialog';
import { NewRequestForm } from './NewRequestForm';
import { money, useRequestSummary, type PurchaseRequest } from './api';
import { useSession } from '../../auth/SessionContext';

/**
 * Purchasing screens.
 *
 * Every one of these renders the same table against the same query — the
 * difference between what an employee and an approver see is enforced by RLS,
 * not by these files. That is why there is no hospital filtering here.
 */

function useTotals() {
  const { data } = useRequestSummary();
  return useMemo(() => {
    const rows = data ?? [];
    const count = (s: string) =>
      rows.filter(r => r.status === s).reduce((n, r) => n + Number(r.total), 0);
    const value = (s: string) =>
      rows.filter(r => r.status === s).reduce((n, r) => n + Number(r.total_value ?? 0), 0);
    return {
      rows,
      pending: count('Pending'),
      approved: count('Approved'),
      chiefApproved: count('Chief approved'),
      rejected: count('Rejected'),
      pendingValue: value('Pending'),
      approvedValue: value('Approved') + value('Chief approved'),
    };
  }, [data]);
}

/** A reviewer's name for the comment trail. */
function useReviewer() {
  const session = useSession();
  return session.user.displayName ?? session.user.username;
}

// -----------------------------------------------------------------------------
// Employee
// -----------------------------------------------------------------------------

/** Employee — their own requests and where each one has got to. */
export function EmployeeDashboard() {
  const t = useTotals();
  return (
    <Stack gap={2.5}>
      <Grid container spacing={2.5}>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <StatTile
            label="Waiting for approval"
            value={t.pending}
            tone="info"
            icon={<ClipboardText size={20} color="currentColor" />}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <StatTile
            label="Approved"
            value={t.approved}
            tone="warning"
            hint="Waiting on the chief approver"
            icon={<TickCircle size={20} color="currentColor" />}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <StatTile
            label="Fully approved"
            value={t.chiefApproved}
            tone="success"
            icon={<TickCircle size={20} color="currentColor" />}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <StatTile
            label="Rejected"
            value={t.rejected}
            tone="error"
            icon={<CloseCircle size={20} color="currentColor" />}
          />
        </Grid>
      </Grid>

      <PageSection
        title="Your requests"
        description="Every request you have raised, newest first."
        disablePadding
      >
        <RequestTable
          showRequester={false}
          emptyTitle="You have not raised any requests"
          emptyDescription="Use “Purchase request” in the sidebar to raise one."
        />
      </PageSection>
    </Stack>
  );
}

/** Employee — raise a request, and see the ones still in flight. */
export function PurchaseRequestScreen() {
  return (
    <Stack gap={2.5}>
      <NewRequestForm />

      <PageSection
        title="Still in flight"
        description="Requests of yours that nobody has finished deciding on."
        disablePadding
      >
        <RequestTable
          status={['Pending', 'Approved']}
          showRequester={false}
          emptyTitle="Nothing in flight"
          emptyDescription="Requests you raise appear here until they are fully approved or rejected."
        />
      </PageSection>
    </Stack>
  );
}

// -----------------------------------------------------------------------------
// Approver
// -----------------------------------------------------------------------------

/** Approver — the queue. This is the screen that does the work. */
export function ApproverPending() {
  const [selected, setSelected] = useState<PurchaseRequest | null>(null);
  const reviewer = useReviewer();
  const t = useTotals();

  return (
    <Stack gap={2.5}>
      {t.pending > 0 && (
        <Alert severity="info">
          {t.pending} {t.pending === 1 ? 'request is' : 'requests are'} waiting for you, worth{' '}
          {money(t.pendingValue)} in total.
        </Alert>
      )}

      <PageSection
        title="Waiting for approval"
        description="Requests raised at your hospital that nobody has reviewed yet."
        disablePadding
      >
        <RequestTable
          status="Pending"
          emptyTitle="Nothing waiting"
          emptyDescription="Every request at your hospital has been reviewed."
          onAction={setSelected}
        />
      </PageSection>

      <DecisionDialog
        request={selected}
        approveAs="Approved"
        reviewerName={reviewer}
        reviewerRole="approver"
        onClose={() => setSelected(null)}
      />
    </Stack>
  );
}

export function ApproverApproved() {
  return (
    <PageSection
      title="Approved"
      description="You have approved these. They now need the chief approver."
      disablePadding
    >
      <RequestTable status={['Approved', 'Chief approved']} emptyTitle="Nothing approved yet" />
    </PageSection>
  );
}

export function ApproverRejected() {
  return (
    <PageSection
      title="Rejected"
      description="Rejected requests, with the reason each was turned down."
      disablePadding
    >
      <RequestTable status="Rejected" emptyTitle="Nothing rejected" />
    </PageSection>
  );
}

// -----------------------------------------------------------------------------
// Chief approver
// -----------------------------------------------------------------------------

/** Chief approver — the second stage, and the only screen this role has. */
export function ChiefApproverDashboard() {
  const [selected, setSelected] = useState<PurchaseRequest | null>(null);
  const reviewer = useReviewer();
  const t = useTotals();

  return (
    <Stack gap={2.5}>
      <Grid container spacing={2.5}>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <StatTile
            label="Awaiting you"
            value={t.approved}
            tone="warning"
            hint="Approved, not yet chief approved"
            icon={<ClipboardText size={20} color="currentColor" />}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <StatTile
            label="Fully approved"
            value={t.chiefApproved}
            tone="success"
            icon={<TickCircle size={20} color="currentColor" />}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <StatTile
            label="Value awaiting you"
            value={money(t.approvedValue)}
            icon={<Wallet3 size={20} color="currentColor" />}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <StatTile
            label="Rejected"
            value={t.rejected}
            tone="error"
            icon={<CloseCircle size={20} color="currentColor" />}
          />
        </Grid>
      </Grid>

      <PageSection
        title="Awaiting chief approval"
        description="An approver has already passed these. Yours is the last decision."
        disablePadding
      >
        <RequestTable
          status="Approved"
          emptyTitle="Nothing awaiting chief approval"
          emptyDescription="Requests appear here once an approver has passed them."
          onAction={setSelected}
        />
      </PageSection>

      <DecisionDialog
        request={selected}
        approveAs="Chief approved"
        reviewerName={reviewer}
        reviewerRole="chief-approver"
        onClose={() => setSelected(null)}
      />
    </Stack>
  );
}

// -----------------------------------------------------------------------------
// Manager
// -----------------------------------------------------------------------------

/** Manager — hospital-wide, but not part of the approval chain. */
export function ManagerDashboard() {
  const t = useTotals();
  return (
    <Stack gap={2.5}>
      <Grid container spacing={2.5}>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <StatTile
            label="Pending"
            value={t.pending}
            tone="info"
            icon={<ClipboardText size={20} color="currentColor" />}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <StatTile label="Approved" value={t.approved} tone="warning" />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <StatTile label="Fully approved" value={t.chiefApproved} tone="success" />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <StatTile
            label="Approved value"
            value={money(t.approvedValue)}
            icon={<Wallet3 size={20} color="currentColor" />}
          />
        </Grid>
      </Grid>

      <PageSection
        title="All requests at your hospital"
        description="Read-only — managers are not part of the approval chain."
        disablePadding
      >
        <RequestTable emptyTitle="No requests" />
      </PageSection>
    </Stack>
  );
}

/** Manager — how the approval chain is performing. */
export function ApproverReports() {
  const t = useTotals();
  const decided = t.chiefApproved + t.rejected;
  const rate = decided > 0 ? Math.round((t.chiefApproved / decided) * 100) : 0;

  return (
    <Stack gap={2.5}>
      <Grid container spacing={2.5}>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <StatTile label="Decided" value={decided} hint="Fully approved or rejected" />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <StatTile
            label="Approval rate"
            value={`${rate}%`}
            tone={rate >= 50 ? 'success' : 'warning'}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <StatTile label="Still waiting" value={t.pending + t.approved} tone="info" />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <StatTile label="Rejected" value={t.rejected} tone="error" />
        </Grid>
      </Grid>

      <PageSection
        title="Sitting with an approver"
        description="Raised but not yet passed to the chief approver. A long list here means the first stage is the bottleneck."
        disablePadding
      >
        <RequestTable status="Pending" emptyTitle="Nothing sitting with an approver" />
      </PageSection>

      <PageSection
        title="Sitting with the chief approver"
        description="Passed by an approver and waiting on the second decision."
        disablePadding
      >
        <RequestTable status="Approved" emptyTitle="Nothing sitting with the chief approver" />
      </PageSection>
    </Stack>
  );
}

/** Manager — what is being bought, broken down by section. */
export function PurchaseReports() {
  const { rows } = useTotals();

  const bySection = useMemo(() => {
    const map = new Map<string, { section: string; total: number; value: number }>();
    for (const r of rows) {
      const key = `${r.hospital ?? '—'} · ${r.section ?? '—'}`;
      const entry = map.get(key) ?? { section: key, total: 0, value: 0 };
      entry.total += Number(r.total);
      entry.value += Number(r.total_value ?? 0);
      map.set(key, entry);
    }
    return [...map.values()].sort((a, b) => b.value - a.value);
  }, [rows]);

  return (
    <Stack gap={2.5}>
      <PageSection
        title="Spending by section"
        description="Every request counted, whatever its status. Aggregated in the database through a security_invoker view, so these are the sections you are allowed to see."
      >
        {bySection.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No requests to report on.
          </Typography>
        ) : (
          <Stack gap={1.25}>
            {bySection.map(row => (
              <Stack
                key={row.section}
                direction="row"
                justifyContent="space-between"
                alignItems="baseline"
                gap={2}
              >
                <Typography variant="body2">{row.section}</Typography>
                <Stack direction="row" gap={2} alignItems="baseline">
                  <Typography variant="caption" color="text.secondary">
                    {row.total} {row.total === 1 ? 'request' : 'requests'}
                  </Typography>
                  <Typography variant="body2">{money(row.value)}</Typography>
                </Stack>
              </Stack>
            ))}
          </Stack>
        )}
      </PageSection>

      <PageSection
        title="Fully approved"
        description="What has actually been committed to."
        disablePadding
      >
        <RequestTable status="Chief approved" emptyTitle="Nothing fully approved yet" />
      </PageSection>
    </Stack>
  );
}

// -----------------------------------------------------------------------------
// Admin
// -----------------------------------------------------------------------------

/**
 * Admin — every request, every hospital.
 *
 * The legacy admin nav had no request view at all: an admin could administer the
 * module but never see its data. Same gap as certifications, closed the same way.
 */
export function AdminAllRequests() {
  const [selected, setSelected] = useState<PurchaseRequest | null>(null);
  const reviewer = useReviewer();

  return (
    <Stack gap={2.5}>
      <PageSection
        title="All purchase requests"
        description="Unscoped — every hospital. Paged, because PostgREST caps a response at 1,000 rows without saying so."
        disablePadding
      >
        <RequestTable emptyTitle="No requests" actionLabel="Open" onAction={setSelected} />
      </PageSection>

      <DecisionDialog
        request={selected}
        approveAs="Approved"
        reviewerName={reviewer}
        reviewerRole="admin"
        onClose={() => setSelected(null)}
      />
    </Stack>
  );
}

export function AdminOverview() {
  const t = useTotals();

  const byHospital = useMemo(() => {
    const map = new Map<
      string,
      { hospital: string; total: number; pending: number; value: number }
    >();
    for (const r of t.rows) {
      const key = r.hospital ?? '—';
      const entry = map.get(key) ?? { hospital: key, total: 0, pending: 0, value: 0 };
      entry.total += Number(r.total);
      entry.value += Number(r.total_value ?? 0);
      if (r.status === 'Pending') entry.pending += Number(r.total);
      map.set(key, entry);
    }
    return [...map.values()].sort((a, b) => b.total - a.total);
  }, [t.rows]);

  return (
    <Stack gap={2.5}>
      <Grid container spacing={2.5}>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <StatTile label="Pending" value={t.pending} tone="info" />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <StatTile label="Approved" value={t.approved} tone="warning" />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <StatTile label="Fully approved" value={t.chiefApproved} tone="success" />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <StatTile label="Rejected" value={t.rejected} tone="error" />
        </Grid>
      </Grid>

      <PageSection
        title="By hospital"
        description="Every request in the system, grouped by where it was raised."
      >
        <Stack gap={1.25}>
          {byHospital.map(row => (
            <Stack
              key={row.hospital}
              direction="row"
              justifyContent="space-between"
              alignItems="baseline"
              gap={2}
            >
              <Typography variant="body2">{row.hospital}</Typography>
              <Stack direction="row" gap={2} alignItems="baseline">
                <Typography variant="caption" color="text.secondary">
                  {row.pending} pending of {row.total}
                </Typography>
                <Typography variant="body2">{money(row.value)}</Typography>
              </Stack>
            </Stack>
          ))}
        </Stack>
      </PageSection>
    </Stack>
  );
}
